/**
 * SponsorsCard — "With Gratitude" crossfade carousel.
 *
 * - Fetches sponsors from /api/public/sponsors (entries with show_on_guest_page: true)
 * - One name shown at a time, 7-second crossfade rotation
 * - Animated gold progress line at bottom
 * - "Meet our gracious sponsors →" link for future sponsors page
 * - Light warm glass treatment
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CYCLE_MS = 7000;

const glassCard = {
  background: 'rgba(255,252,240,0.55)',
  backdropFilter: 'blur(16px) saturate(1.30) brightness(1.04)',
  WebkitBackdropFilter: 'blur(16px) saturate(1.30) brightness(1.04)',
  border: '1px solid rgba(200,170,60,0.45)',
  borderRadius: 20,
  boxShadow: [
    'inset 0 1.5px 0 rgba(255,255,255,0.85)',
    'inset 0 -1px 0 rgba(200,170,60,0.22)',
    '0 8px 32px rgba(140,100,20,0.12)',
    '0 0 0 0.5px rgba(200,170,60,0.20)',
  ].join(', '),
};

const SponsorsCard = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [visible, setVisible]   = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef  = useRef(null);
  const frameRef  = useRef(null);
  const startRef  = useRef(null);

  // Fetch sponsors
  useEffect(() => {
    fetch(`${API_URL}/api/public/sponsors`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setSponsors(data); })
      .catch(() => {});
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (sponsors.length < 2) return;

    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const pct = Math.min(elapsed / CYCLE_MS, 1);
      setProgress(pct);

      if (pct < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        // Crossfade out
        setVisible(false);
        setTimeout(() => {
          setActiveIdx(i => (i + 1) % sponsors.length);
          setVisible(true);
          startRef.current = null;
          setProgress(0);
          frameRef.current = requestAnimationFrame(animate);
        }, 500);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(timerRef.current);
    };
  }, [sponsors]);

  const currentName = sponsors.length > 0
    ? sponsors[activeIdx]?.name || ''
    : null;

  return (
    <div className="card-h-pad" style={{ width: '100%', paddingTop: 16, paddingBottom: 0, boxSizing: 'border-box' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Allura&family=Cormorant+Garamond:wght@600;700&display=swap');`}</style>
      <div style={{ ...glassCard, padding: '16px 20px', textAlign: 'center', position: 'relative', overflow: 'visible' }}>

        {/* 4a — "With Gratitude" title: matches MessageForm "Messages & Questions" style */}
        {/* 4b — Heart icons replacing Wine */}
        {/* 4c — NO divider below title */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8, position:'relative', zIndex:1 }}>
          <Heart size={15} style={{ color: 'rgba(180,140,30,0.75)' }} />
          <p style={{ fontFamily:"'Cinzel', serif", fontSize: 15, fontWeight: 700,
            color: '#1E1A14', margin: 0, letterSpacing: '2px', textTransform: 'uppercase',
            textShadow: '0 1px 0 rgba(255,255,255,0.70)' }}>
            With Gratitude
          </p>
          <Heart size={15} style={{ color: 'rgba(180,140,30,0.75)' }} />
        </div>

        {/* Gift boxes — row above the text, not overlapping */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6, pointerEvents:'none' }}>
          <img src="https://customer-assets.emergentagent.com/job_49133e57-f6cd-4b7b-9084-054c1edd8062/artifacts/zhl3mucp_giftbox.png"
            alt="" style={{ width:55, height:'auto', opacity:0.9 }} />
          <img src="https://customer-assets.emergentagent.com/job_49133e57-f6cd-4b7b-9084-054c1edd8062/artifacts/zhl3mucp_giftbox.png"
            alt="" style={{ width:55, height:'auto', opacity:0.9 }} />
        </div>

        {/* 4d/4e — Sponsor name or Thank you text */}
        <div style={{ minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, position:'relative', zIndex:1 }}>
          {currentName ? (
            <p style={{
              fontFamily: "'Allura', cursive",
              fontSize: 28, fontWeight: 400,
              color: '#1E1A14', margin: 0,
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.50s ease',
              textAlign: 'center', lineHeight: 1.2,
            }}>
              {currentName}
            </p>
          ) : (
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontWeight: 600, fontStyle: 'italic',
              color: '#1E1A14', margin: 0, textAlign: 'center', lineHeight: 1.3 }}>
              Thank you for making this celebration possible.
            </p>
          )}
        </div>

        {/* Sponsor count */}
        {sponsors.length > 1 && (
          <p style={{ fontSize: 11, color: 'rgba(120,90,20,0.60)', margin: '0 0 14px',
            letterSpacing: '0.5px', fontStyle: 'italic' }}>
            {activeIdx + 1} of {sponsors.length}
          </p>
        )}

        {/* Animated gold progress line */}
        {sponsors.length > 1 && (
          <div style={{ width: '60%', height: 1.5, background: 'rgba(200,170,60,0.20)',
            borderRadius: 1, margin: '0 auto 16px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progress * 100}%`,
              background: 'linear-gradient(90deg, rgba(200,170,60,0.60), rgba(220,190,60,0.90))',
              borderRadius: 1,
              transition: 'width 0.05s linear',
            }} />
          </div>
        )}

        {/* 4f — "Meet our gracious sponsors" as a styled gold button, delicate size */}
        <button
          onClick={() => { window.location.href = "https://sponsors.80yearsofconnection.com"; }}
          style={{
            padding: '10px 22px', borderRadius: 22, cursor: 'pointer',
            background: 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
            border: '1px solid rgba(200,170,60,0.65)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.22), inset 0 1px 0 rgba(212,168,83,0.15)',
            fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700,
            color: '#D4A853', letterSpacing: '1.2px', textTransform: 'uppercase',
            transition: 'filter 0.2s ease, transform 0.2s ease',
            position:'relative', zIndex:1,
          }}
          onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.15)'; e.currentTarget.style.transform='translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter=''; e.currentTarget.style.transform=''; }}
        >
          Meet Our Gracious Sponsors
        </button>

      </div>
    </div>
  );
};

export default SponsorsCard;
